import { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    user_type: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, user_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        user_type: 'general',
      });
    } catch (err) {
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          const messages = detail.map(d => d.msg).join('. ');
          setError(messages);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Failed to submit form. Please check your inputs and try again.');
        }
      } else {
        setError('Failed to submit form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
            Get In Touch
          </span>
          <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-none text-white mb-6">
            Contact Us
          </h1>
          <p className="font-body text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Have questions about MeritumPraxis? We'd love to hear from you. 
            Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-8">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-meritum-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-meritum-teal" size={20} />
                  </div>
                  <div>
                    <h3 className="font-body text-sm font-semibold text-meritum-navy mb-1">
                      Email
                    </h3>
                    <a
                      href="mailto:info@meritumpraxis.co.za"
                      className="font-body text-slate-600 hover:text-meritum-teal transition-colors"
                      data-testid="contact-email-link"
                    >
                      info@meritumpraxis.co.za
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-meritum-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-meritum-gold" size={20} />
                  </div>
                  <div>
                    <h3 className="font-body text-sm font-semibold text-meritum-navy mb-1">
                      Location
                    </h3>
                    <p className="font-body text-slate-600">
                      South Africa
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-12 p-6 bg-slate-50 rounded-lg">
                <h3 className="font-heading text-lg font-semibold text-meritum-navy mb-4">
                  Response Time
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  We typically respond to all inquiries within 1-2 business days. 
                  For urgent matters, please indicate so in your message.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 p-8 md:p-12 rounded-lg">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="text-green-600" size={40} />
                    </div>
                    <h3 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                      Message Sent Successfully!
                    </h3>
                    <p className="font-body text-slate-600 mb-8">
                      Thank you for contacting us. We'll get back to you shortly.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      className="bg-meritum-teal text-white hover:bg-meritum-navy font-body font-medium"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-8">
                      Send Us a Message
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="font-body text-sm font-medium text-meritum-navy">
                            Full Name and Surname *
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your full name and surname"
                            className="border-slate-300 focus:border-meritum-teal"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="font-body text-sm font-medium text-meritum-navy">
                            Email Address *
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your@email.com"
                            className="border-slate-300 focus:border-meritum-teal"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="subject" className="font-body text-sm font-medium text-meritum-navy">
                            Subject *
                          </Label>
                          <Input
                            id="subject"
                            name="subject"
                            type="text"
                            required
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder="What is this about?"
                            className="border-slate-300 focus:border-meritum-teal"
                            data-testid="contact-subject-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user_type" className="font-body text-sm font-medium text-meritum-navy">
                            I am a...
                          </Label>
                          <Select value={formData.user_type} onValueChange={handleSelectChange}>
                            <SelectTrigger className="border-slate-300 focus:border-meritum-teal" data-testid="contact-user-type-select">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General Inquiry</SelectItem>
                              <SelectItem value="candidate">Candidate</SelectItem>
                              <SelectItem value="organization">Organization</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="font-body text-sm font-medium text-meritum-navy">
                          Message *
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us how we can help you..."
                          rows={6}
                          className="border-slate-300 focus:border-meritum-teal resize-none"
                          data-testid="contact-message-input"
                        />
                      </div>

                      {error && (
                        <p className="font-body text-sm text-red-600" data-testid="contact-error">
                          {error}
                        </p>
                      )}

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full md:w-auto bg-meritum-teal text-white hover:bg-meritum-navy font-body font-bold px-8 py-6 text-base"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Send Message
                            <Send className="ml-2" size={18} />
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
